// FinanceFees.jsx — mirrors admin Fees.jsx exactly
// All mutations go through coordinator request/approval workflow

import { sortByName, sortAsc } from '../utils/sortOptions';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon,
  BanknotesIcon, ExclamationTriangleIcon, DocumentTextIcon,
  XMarkIcon, CheckCircleIcon, PlusCircleIcon, TagIcon,
  PencilSquareIcon, PrinterIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';

/* ── helpers ───────────────────────────────────────────────────────────── */
const fmtMoney = n => `Rs. ${Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:0})}`;
const fmtHostelMoney = n => `Rs. ${Number(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const fmtShort = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'2-digit',month:'short'}) : '—';
const ini      = n => (n||'').split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';
const monthLabel = (m) => { if(!m) return '—'; const [y,mn]=String(m).split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'short',year:'numeric'}); };
// Library/hostel fines are pushed here automatically from those panels (tagged
// module:'library'/'hostel' at the source); anything else falls under "Other".
const finesByModule = (fines, mod) => (fines||[]).filter(f => f.module === mod);
const otherFines     = (fines) => (fines||[]).filter(f => f.module !== 'library' && f.module !== 'hostel');
const sumOf           = (list) => list.reduce((s,f) => s + Number(f.amount||0), 0);
const fineLabel       = (f) => f.module === 'library' ? 'Library Fine' : f.module === 'hostel' ? 'Hostel Fine' : 'Fine';

const exportCSV = (rows, name) => {
  if (!rows.length) return;
  const h = Object.keys(rows[0]);
  const e = v => { const s=String(v??''); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
  const csv = [h.join(','), ...rows.map(r=>h.map(k=>e(r[k])).join(','))].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8;'}));
  a.download = name+'.csv'; a.click();
};

/* ── styles ────────────────────────────────────────────────────────────── */
const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ff-root*{box-sizing:border-box;}
  .ff-root{font-family:'Sora',sans-serif;color:#1a1d2e;animation:ffFade .4s ease both;}
  @keyframes ffFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .ff-hdr{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .ff-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .ff-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .req-banner{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:9px 14px;font-size:.76rem;color:#9a3412;font-weight:600;margin-bottom:1.2rem;}
  .stat-row{display:grid;grid-template-columns:repeat(4,1fr);gap:.9rem;margin-bottom:1.4rem;}
  .stat-card{background:white;border-radius:14px;padding:1rem 1.1rem;border:1px solid #e8ebf4;position:relative;overflow:hidden;}
  .stat-card::after{content:'';position:absolute;top:0;right:0;width:60px;height:60px;border-radius:0 14px 0 60px;opacity:.07;}
  .stat-card.blue::after{background:#3b6ef8;} .stat-card.green::after{background:#10c98e;}
  .stat-card.amber::after{background:#f59e0b;} .stat-card.red::after{background:#ef4444;}
  .sc-icon{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:.6rem;}
  .sc-icon svg{width:14px;height:14px;stroke-width:1.8;}
  .sc-icon.blue{background:#eef2ff;color:#3b6ef8;} .sc-icon.green{background:#ecfdf5;color:#10c98e;}
  .sc-icon.amber{background:#fffbeb;color:#f59e0b;} .sc-icon.red{background:#fef2f2;color:#ef4444;}
  .sc-lbl{font-size:.66rem;font-weight:600;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;margin-bottom:2px;}
  .sc-val{font-size:1rem;font-weight:700;color:#0f1123;font-family:'JetBrains Mono',monospace;}
  .tab-bar{display:flex;gap:4px;background:#f0f2f7;border-radius:11px;padding:4px;width:fit-content;margin-bottom:1.2rem;}
  .tab-btn{padding:7px 18px;border-radius:8px;border:none;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;transition:all .15s;background:transparent;color:#9094a8;}
  .tab-btn.active{background:white;color:#0f1123;box-shadow:0 1px 4px rgba(0,0,0,.1);}
  .ff-toolbar{display:flex;gap:.7rem;flex-wrap:wrap;align-items:center;margin-bottom:1.2rem;}
  .search-wrap{position:relative;flex:1;max-width:300px;}
  .search-wrap svg{position:absolute;left:11px;top:50%;transform:translateY(-50%);width:14px;height:14px;stroke:#9094a8;pointer-events:none;}
  .search-inp{font-family:'Sora',sans-serif;font-size:.8rem;width:100%;padding:8px 12px 8px 32px;border:1.5px solid #e0e4f0;border-radius:10px;background:white;outline:none;}
  .search-inp:focus{border-color:#3b6ef8;}
  .sel{font-family:'Sora',sans-serif;font-size:.78rem;padding:8px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:white;outline:none;}
  .sel:focus{border-color:#3b6ef8;}
  .btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border:none;border-radius:10px;font-family:'Sora',sans-serif;font-size:.8rem;font-weight:600;cursor:pointer;transition:.15s;}
  .btn svg{width:14px;height:14px;stroke-width:2;}
  .btn-blue{background:#3b6ef8;color:white;} .btn-blue:hover{box-shadow:0 4px 14px rgba(59,110,248,.3);}
  .btn-green{background:#10c98e;color:white;} .btn-green:hover{box-shadow:0 4px 14px rgba(16,201,142,.3);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-amber{background:#fffbeb;color:#d97706;border:1px solid #fde68a;} .btn-amber:hover{background:#d97706;color:white;}
  .btn:disabled{opacity:.55;cursor:not-allowed;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .panel-hdr{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;gap:8px;}
  .panel-title{font-size:.86rem;font-weight:600;color:#1a1d2e;}
  .panel-sub{font-size:.7rem;color:#9094a8;margin-top:1px;}
  .tbl{width:100%;border-collapse:collapse;}
  .tbl thead{background:#fafbff;border-bottom:1px solid #edf0f9;}
  .tbl th{padding:10px 14px;text-align:center !important;font-size:.64rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{ padding:10px 14px;font-size:.8rem;vertical-align:middle; text-align:center !important; }
  .name-cell{display:flex;align-items:center;gap:9px;}
  .av{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#3b6ef8,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:.66rem;font-weight:700;color:white;flex-shrink:0;}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ns{font-size:.68rem;color:#9094a8;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.76rem;color:#6b7194;}
  .tag{display:inline-flex;font-size:.67rem;font-weight:600;padding:2px 8px;border-radius:99px;white-space:nowrap;}
  .tag-blue{background:#eef2ff;color:#3b6ef8;} .tag-purple{background:#f3f0ff;color:#8b5cf6;}
  .status-pill{display:inline-flex;font-size:.67rem;font-weight:600;padding:3px 10px;border-radius:99px;white-space:nowrap;}
  .status-pill.paid{background:#ecfdf5;color:#10c98e;} .status-pill.unpaid{background:#fef2f2;color:#ef4444;}
  .status-pill.partial{background:#fffbeb;color:#f59e0b;} .status-pill.waived{background:#f3f0ff;color:#8b5cf6;}
  .status-pill.overdue{background:#fef2f2;color:#dc2626;}
  .amt-due{font-family:'JetBrains Mono',monospace;font-size:.8rem;font-weight:700;color:#0f1123;}
  .amt-paid{font-family:'JetBrains Mono',monospace;font-size:.76rem;color:#10c98e;}
  .amt-fine{font-family:'JetBrains Mono',monospace;font-size:.73rem;color:#ef4444;}
  .fine-chip{display:inline-flex;align-items:center;gap:5px;font-size:.68rem;font-weight:600;padding:2px 7px;border-radius:6px;background:#fff7ed;color:#ea580c;margin:1px 0;}
  .fine-chip.library{background:#faf5ff;color:#9333ea;}
  .fine-chip.hostel{background:#eef2ff;color:#3b6ef8;}
  .fine-cell-items{display:flex;flex-direction:column;align-items:flex-start;gap:2px;margin-top:2px;}
  .act-row{display:flex;gap:4px;flex-wrap:wrap;}
  .act-btn{width:28px;height:28px;border-radius:7px;border:1px solid #e4e7f0;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:.15s;flex-shrink:0;}
  .act-btn svg{width:13px;height:13px;stroke-width:2;}
  .act-btn.pay{color:#10c98e;} .act-btn.pay:hover{background:#ecfdf5;border-color:#10c98e;}
  .act-btn.fine{color:#ef4444;} .act-btn.fine:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.disc{color:#8b5cf6;} .act-btn.disc:hover{background:#f3f0ff;border-color:#ddd6fe;}
  .act-btn.edit{color:#3b6ef8;} .act-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;}
  .act-btn.print{color:#6b7194;} .act-btn.print:hover{background:#f0f2f7;border-color:#9094a8;}
  .act-btn.csv{color:#6b7194;} .act-btn.csv:hover{background:#f0f2f7;border-color:#9094a8;}
  .proof-img{width:48px;height:36px;border-radius:6px;object-fit:cover;cursor:pointer;border:1px solid #e0e4f0;}
  .proof-placeholder{font-size:.68rem;color:#c0c5dc;}
  .overdue-tag{display:inline-flex;font-size:.64rem;font-weight:700;padding:2px 6px;border-radius:99px;background:#fef2f2;color:#dc2626;margin-left:4px;}
  .empty{text-align:center;padding:3rem 2rem;color:#9094a8;font-size:.82rem;}
  .spin{width:18px;height:18px;border:2px solid #e4e7f0;border-top-color:#3b6ef8;border-radius:50%;animation:sp .7s linear infinite;}
  @keyframes sp{to{transform:rotate(360deg);}}
  /* Modals */
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.6);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;overflow-y:auto;}
  .modal{background:white;border-radius:20px;width:100%;max-height:90vh;display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(0,0,0,.2);animation:mIn .25s ease;overflow:hidden;}
  @keyframes mIn{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:scale(1)}}
  .mo-hd{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;}
  .mo-title{font-size:.95rem;font-weight:700;color:#0f1123;}
  .mo-close{width:30px;height:30px;border-radius:8px;border:none;background:#f0f2f7;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#6b7194;}
  .mo-close svg{width:14px;height:14px;stroke-width:2;}
  .mo-bd{padding:20px 22px;overflow-y:auto;flex:1;}
  .mo-ft{display:flex;gap:8px;justify-content:flex-end;padding:14px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;}
  .form-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.68rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fs{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 11px;border:1.5px solid #e0e4f0;border-radius:9px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;}
  .fi:focus,.fs:focus{border-color:#3b6ef8;background:white;}
  .fi[readonly]{background:#f0f2f7;color:#9094a8;cursor:not-allowed;}
  .info-row{display:flex;justify-content:space-between;font-size:.8rem;padding:6px 0;border-bottom:1px solid #f3f5fb;}
  .info-row:last-child{border-bottom:none;}
  .info-lbl{color:#9094a8;font-weight:600;} .info-val{font-weight:600;color:#0f1123;}
  .req-note{background:#fff7ed;border:1px solid #fed7aa;border-radius:9px;padding:9px 12px;font-size:.74rem;color:#9a3412;font-weight:600;margin-bottom:12px;}

  .ff-cards{display:none;padding:10px;}
  .ff-card{background:white;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .ff-card-top{display:flex;align-items:flex-start;justify-content:space-between;gap:8px;margin-bottom:8px;}
  .ff-card-row{display:flex;justify-content:space-between;align-items:center;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;}
  .ff-card-row:last-of-type{border-bottom:none;}
  .ff-card-label{color:#9094a8;font-weight:600;}
  .ff-card-actions{display:flex;gap:5px;margin-top:8px;flex-wrap:wrap;}

  @media(max-width:900px){.stat-row{grid-template-columns:repeat(2,1fr);}}
  @media(max-width:768px){
    .tbl-wrap{display:none !important;}
    .ff-cards{display:block;}
  }
  @media(max-width:600px){.stat-row{grid-template-columns:1fr 1fr;}.form-grid{grid-template-columns:1fr;}.ff.full{grid-column:1;}.tbl-wrap{overflow-x:auto;}}
`;

// One of the three fine-source columns (Library / Hostel / Other) — shows the
// column's total plus each itemized fine underneath, or a dash when there's none.
const FineCell = ({ list, chipClass }) => {
  if (!list.length) return <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>;
  return (
    <div>
      <span className="amt-fine">+{fmtMoney(sumOf(list))}</span>
      <div className="fine-cell-items">
        {list.map(f => (
          <span key={f.id||f._id} className={`fine-chip ${chipClass}`}>{f.reason}: {fmtMoney(f.amount)}</span>
        ))}
      </div>
    </div>
  );
};

/* ── component ─────────────────────────────────────────────────────────── */
export default function FinanceFees() {
  const [tab,         setTab]         = useState('all');
  const [records,     setRecords]     = useState([]);
  const [stats,       setStats]       = useState(null);
  const [classes,     setClasses]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [search,      setSearch]      = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSec,   setFilterSec]   = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterStatus,setFilterStatus]= useState('');
  // Modals
  const [payModal,    setPayModal]    = useState(null);
  const [fineModal,   setFineModal]   = useState(null);
  const [discModal,   setDiscModal]   = useState(null);
  const [editModal,   setEditModal]   = useState(null);
  const [proofModal,  setProofModal]  = useState(null);
  const [bulkModal,   setBulkModal]   = useState(false);
  // Forms
  const [payForm,     setPayForm]     = useState({ amountPaid:'', paymentDate:'', paymentMethod:'cash', transactionId:'' });
  const [fineForm,    setFineForm]    = useState({ fineAmount:'', fineReason:'' });
  const [discForm,    setDiscForm]    = useState({ discountAmount:'', discountReason:'' });
  const [editForm,    setEditForm]    = useState({});
  const [bulkForm,    setBulkForm]    = useState({ classId:'', section:'', month:'', feeAmount:'', dueDate:'' });

  const searchTimer = useRef(null);

  useEffect(() => { loadClasses(); }, []);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { loadRecords(); }, 300);
  }, [tab, filterClass, filterSec, filterMonth, filterStatus, search]);

  const loadClasses = async () => {
    try { const { data } = await api.get('/finance/classes'); setClasses(Array.isArray(data) ? data : []); } catch {}
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClass)  params.append('classId', filterClass);
      if (filterSec)    params.append('section', filterSec);
      if (filterMonth)  params.append('month',   filterMonth);
      if (filterStatus) params.append('status',  filterStatus);
      if (search)       params.append('search',  search);
      if (tab === 'defaulters') params.append('defaulters', 'true');
      if (tab === 'proofs')     params.append('proofs',     'true');
      const { data } = await api.get(`/finance/fees?${params}`);
      const list = Array.isArray(data) ? data : (data.records || []);
      setRecords(list);
      if (data.stats) setStats(data.stats);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const sections = classes.find(c => String(c._id) === String(filterClass))?.sections || [];

  /* ── Mutations → requests ──────────────────────────────────────────────── */

  const handleMarkPaid = async () => {
    if (!payForm.amountPaid) return toast.error('Enter amount');
    setSaving(true);
    try {
      await api.post('/finance/requests', {
        type: 'fee_mark_paid',
        payload: {
          feeId:         payModal._id,
          studentName:   payModal.student?.user?.name,
          admissionNo:   payModal.student?.admissionNo,
          month:         payModal.month,
          ...payForm,
          amountPaid: Number(payForm.amountPaid),
        },
        note: `Mark paid — ${payModal.student?.user?.name} (${payModal.month})`,
      });
      toast.success('Mark paid request submitted — awaiting admin approval');
      setPayModal(null);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handleAddFine = async () => {
    if (!fineForm.fineAmount) return toast.error('Enter fine amount');
    if (!fineForm.fineReason.trim()) return toast.error('Reason is required');
    setSaving(true);
    try {
      await api.post('/finance/requests', {
        type: 'fee_add_fine',
        payload: {
          feeId:       fineModal._id,
          studentName: fineModal.student?.user?.name,
          admissionNo: fineModal.student?.admissionNo,
          month:       fineModal.month,
          ...fineForm,
          fineAmount: Number(fineForm.fineAmount),
        },
        note: `Add fine — ${fineModal.student?.user?.name} (${fineModal.month})`,
      });
      toast.success('Add fine request submitted — awaiting admin approval');
      setFineModal(null);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handleDiscount = async () => {
    if (!discForm.discountAmount) return toast.error('Enter discount amount');
    if (!discForm.discountReason.trim()) return toast.error('Reason is required');
    setSaving(true);
    try {
      await api.post('/finance/requests', {
        type: 'fee_discount',
        payload: {
          feeId:       discModal._id,
          studentName: discModal.student?.user?.name,
          admissionNo: discModal.student?.admissionNo,
          month:       discModal.month,
          ...discForm,
          discountAmount: Number(discForm.discountAmount),
        },
        note: `Discount — ${discModal.student?.user?.name} (${discModal.month})`,
      });
      toast.success('Discount request submitted — awaiting admin approval');
      setDiscModal(null);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handleEditRecord = async () => {
    setSaving(true);
    try {
      await api.post('/finance/requests', {
        type: 'fee_edit',
        payload: {
          feeId:       editModal._id,
          studentName: editModal.student?.user?.name,
          admissionNo: editModal.student?.admissionNo,
          month:       editModal.month,
          ...editForm,
          feeAmount:  Number(editForm.feeAmount),
          totalDue:   Number(editForm.totalDue),
        },
        note: `Edit record — ${editModal.student?.user?.name} (${editModal.month})`,
      });
      toast.success('Edit request submitted — awaiting admin approval');
      setEditModal(null);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handleBulkGenerate = async () => {
    if (!bulkForm.classId || !bulkForm.month || !bulkForm.feeAmount) return toast.error('Fill all required fields');
    setSaving(true);
    try {
      await api.post('/finance/requests', {
        type: 'fee_bulk_generate',
        payload: {
          ...bulkForm,
          feeAmount: Number(bulkForm.feeAmount),
        },
        note: `Bulk generate — ${bulkForm.month}`,
      });
      toast.success('Bulk generate request submitted — awaiting admin approval');
      setBulkModal(false);
    } catch { toast.error('Failed to submit request'); }
    finally { setSaving(false); }
  };

  const handlePrintChallan = (rec) => {
    const w = window.open('', '_blank');
    if (!w) return;
    const name = rec.student?.user?.name || '—';
    const adm  = rec.student?.admissionNo || '—';
    const cls  = rec.class?.name || '—';
    const sec  = rec.student?.section || '—';
    w.document.write(`<!DOCTYPE html><html><head><title>Fee Challan</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;max-width:500px;margin:auto;}
      h2{text-align:center;border-bottom:2px solid #333;padding-bottom:8px;}
      table{width:100%;border-collapse:collapse;margin-top:16px;}
      td{padding:8px 4px;border-bottom:1px solid #eee;font-size:14px;}
      td:first-child{color:#555;width:160px;}
      td:last-child{font-weight:600;}
      .total-row td{ font-size:16px;font-weight:700;border-top:2px solid #333;padding-top:12px; text-align:center !important; }
      .footer{margin-top:32px;text-align:center;font-size:12px;color:#777;}
      @media print{@page{margin:24px}}</style></head><body>
      <h2>Fee Challan</h2>
      <table>
        <tr><td>Student Name</td><td>${name}</td></tr>
        <tr><td>Admission No</td><td>${adm}</td></tr>
        <tr><td>Class / Section</td><td>${cls} / ${sec}</td></tr>
        <tr><td>Month</td><td>${monthLabel(rec.month)}</td></tr>
        <tr><td>Fee Amount</td><td>${fmtMoney(rec.feeAmount)}</td></tr>
        <tr><td>Hostel Fee</td><td>${fmtHostelMoney(rec.hostelFee)}</td></tr>
        ${rec.fines?.length?`<tr><td>Fines</td><td>${rec.fines.map(f=>`${fineLabel(f)}: ${f.reason} — ${fmtMoney(f.amount)}`).join('<br/>')}</td></tr>`:''}
        ${rec.discount?`<tr><td>Discount</td><td>-${fmtMoney(rec.discount)}</td></tr>`:''}
        <tr class="total-row"><td>Total Due</td><td>${fmtMoney(rec.totalDue)}</td></tr>
        <tr><td>Amount Paid</td><td>${fmtMoney(rec.amountPaid)}</td></tr>
        <tr><td>Balance</td><td>${fmtMoney((rec.totalDue||0)-(rec.amountPaid||0))}</td></tr>
        <tr><td>Status</td><td>${rec.status||'—'}</td></tr>
        <tr><td>Due Date</td><td>${fmtDate(rec.dueDate)}</td></tr>
      </table>
      <div class="footer">Generated on ${new Date().toLocaleDateString('en-PK')}</div>
      <script>window.onload=()=>{window.print();}<\/script></body></html>`);
    w.document.close();
  };

  const handleExportCSV = (rec) => {
    exportCSV([{
      Name: rec.student?.user?.name||'', 'Admission No': rec.student?.admissionNo||'',
      Class: rec.class?.name||'', Section: rec.student?.section||'',
      Month: rec.month||'', 'Fee Amount': rec.feeAmount||0, 'Hostel Fee': Number(rec.hostelFee||0).toFixed(2),
      Fines: rec.fines?.reduce((s,f)=>s+f.amount,0)||0,
      Discount: rec.discount||0, 'Total Due': rec.totalDue||0,
      'Amount Paid': rec.amountPaid||0, Status: rec.status||'',
      'Due Date': fmtDate(rec.dueDate), 'Payment Method': rec.paymentMethod||'',
    }], `fee_${rec.student?.admissionNo||'student'}_${rec.month||'month'}`);
  };

  const isOverdue = (r) => r.status !== 'paid' && r.dueDate && new Date(r.dueDate) < new Date();

  const filtered = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.student?.user?.name||'').toLowerCase().includes(q) ||
           (r.student?.admissionNo||'').toLowerCase().includes(q);
  });

  const statsData = stats || {
    total:    records.length,
    paid:     records.filter(r=>r.status==='paid').length,
    unpaid:   records.filter(r=>r.status==='unpaid'||r.status==='partial').length,
    overdue:  records.filter(r=>isOverdue(r)).length,
    collected:records.filter(r=>r.status==='paid').reduce((s,r)=>s+(r.amountPaid||0),0),
    pending:  records.filter(r=>r.status!=='paid').reduce((s,r)=>s+((r.totalDue||0)-(r.amountPaid||0)),0),
  };

  return (
    <div className="ff-root"><style>{style}</style>

      <div className="ff-hdr">
        <div>
          <div className="ff-title">Fee Management</div>
          <div className="ff-sub">View, track and manage student fee records.</div>
        </div>
        <button className="btn btn-blue" onClick={() => { setBulkForm({ classId:'', section:'', month:'', feeAmount:'', dueDate:'' }); setBulkModal(true); }}>
          <PlusCircleIcon />Bulk Generate
        </button>
      </div>

      <div className="req-banner">📋 Mark paid, add fine, discount, edit and bulk generate are submitted as requests — admin must approve before taking effect.</div>

      {/* Stat cards */}
      <div className="stat-row">
        <div className="stat-card blue"><div className="sc-icon blue"><DocumentTextIcon/></div><div className="sc-lbl">Total Records</div><div className="sc-val">{statsData.total}</div></div>
        <div className="stat-card green"><div className="sc-icon green"><CheckCircleIcon/></div><div className="sc-lbl">Paid</div><div className="sc-val">{statsData.paid}</div></div>
        <div className="stat-card amber"><div className="sc-icon amber"><BanknotesIcon/></div><div className="sc-lbl">Collected</div><div className="sc-val" style={{fontSize:'.82rem'}}>{fmtMoney(statsData.collected)}</div></div>
        <div className="stat-card red"><div className="sc-icon red"><ExclamationTriangleIcon/></div><div className="sc-lbl">Pending</div><div className="sc-val" style={{fontSize:'.82rem'}}>{fmtMoney(statsData.pending)}</div></div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        {[{k:'all',l:'All Records'},{k:'defaulters',l:'Defaulters'},{k:'proofs',l:'Payment Proofs'}].map(t=>(
          <button key={t.k} className={`tab-btn${tab===t.k?' active':''}`} onClick={()=>setTab(t.k)}>{t.l}</button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="ff-toolbar">
        <div className="search-wrap">
          <MagnifyingGlassIcon/>
          <input className="search-inp" placeholder="Search student name or admission no…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select className="sel" value={filterClass} onChange={e=>{setFilterClass(e.target.value);setFilterSec('');}}>
          <option value="">All Classes</option>
          {sortByName(classes).map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        <select className="sel" value={filterSec} onChange={e=>setFilterSec(e.target.value)} disabled={!filterClass}>
          <option value="">{filterClass?'All Sections':'— Sec —'}</option>
          {sortAsc(sections).map(s=><option key={s} value={s}>Section {s}</option>)}
        </select>
        <input type="month" className="sel" value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}/>
        {tab==='all'&&(
          <select className="sel" value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="unpaid">Unpaid</option>
            <option value="waived">Waived</option>
          </select>
        )}
        {(search||filterClass||filterSec||filterMonth||filterStatus) && (
          <button className="btn btn-ghost" onClick={()=>{setSearch('');setFilterClass('');setFilterSec('');setFilterMonth('');setFilterStatus('');}}>Clear</button>
        )}
        <button className="btn btn-ghost" style={{marginLeft:'auto'}} onClick={()=>exportCSV(filtered.map(r=>({
          Name:r.student?.user?.name||'',AdmissionNo:r.student?.admissionNo||'',
          Class:r.class?.name||'',Section:r.student?.section||'',
          Month:r.month||'',FeeAmount:r.feeAmount||0,HostelFee:Number(r.hostelFee||0).toFixed(2),Fines:r.fines?.reduce((s,f)=>s+f.amount,0)||0,
          Discount:r.discount||0,TotalDue:r.totalDue||0,AmountPaid:r.amountPaid||0,
          Status:r.status||'',DueDate:fmtDate(r.dueDate),
        })),'fees_export')}>
          <ArrowDownTrayIcon/>Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="panel">
        <div className="panel-hdr">
          <div><div className="panel-title">{tab==='all'?'All Fee Records':tab==='defaulters'?'Defaulters':'Payment Proofs'}</div><div className="panel-sub">{loading?'Loading…':`${filtered.length} records`}</div></div>
        </div>

        {loading ? (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px 0',color:'#9094a8'}}>
            <div className="spin"/>Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">No records found.</div>
        ) : (
          <div className="tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr>
                <th>Student</th><th>Class</th><th>Month</th><th>Fee</th>
                <th>Hostel Fee</th><th>Library Fine</th><th>Hostel Fine</th><th>Other Fines</th><th>Discount</th><th>Total Due</th><th>Paid</th>
                <th>Status</th><th>Due Date</th>
                {tab==='proofs'&&<th>Proof</th>}
                <th>Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map(r=>(
                  <tr key={r._id}>
                    <td>
                      <div className="name-cell">
                        <div className="av">{ini(r.student?.user?.name)}</div>
                        <div><div className="nm">{r.student?.user?.name||'—'}</div><div className="ns mono">{r.student?.admissionNo}</div></div>
                      </div>
                    </td>
                    <td>
                      <span className="tag tag-blue">{r.class?.name||'—'}</span>
                      {r.student?.section&&<span className="tag tag-purple" style={{marginLeft:4}}>Sec {r.student.section}</span>}
                    </td>
                    <td><span className="mono">{monthLabel(r.month)}</span></td>
                    <td><span className="amt-due">{fmtMoney(r.feeAmount)}</span></td>
                    <td><span className="amt-due">{fmtHostelMoney(r.hostelFee)}</span></td>
                    <td><FineCell list={finesByModule(r.fines,'library')} chipClass="library" /></td>
                    <td><FineCell list={finesByModule(r.fines,'hostel')} chipClass="hostel" /></td>
                    <td><FineCell list={otherFines(r.fines)} chipClass="" /></td>
                    <td>
                      {r.discount
                        ? <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.73rem',color:'#8b5cf6'}}>-{fmtMoney(r.discount)}</span>
                        : <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>}
                    </td>
                    <td><span className="amt-due">{fmtMoney(r.totalDue)}</span></td>
                    <td><span className="amt-paid">{fmtMoney(r.amountPaid)}</span></td>
                    <td>
                      <span className={`status-pill ${r.status||'unpaid'}`}>{r.status||'unpaid'}</span>
                      {isOverdue(r)&&<span className="overdue-tag">overdue</span>}
                    </td>
                    <td>
                      <span className="mono" style={{color:isOverdue(r)?'#dc2626':'#6b7194'}}>{fmtDate(r.dueDate)}</span>
                    </td>
                    {tab==='proofs'&&(
                      <td>
                        {r.paymentProof
                          ? <img src={r.paymentProof} alt="proof" className="proof-img" onClick={()=>setProofModal(r.paymentProof)}/>
                          : <span className="proof-placeholder">No proof</span>}
                      </td>
                    )}
                    <td>
                      <div className="act-row">
                        <button className="act-btn pay"   title="Request Mark Paid"  onClick={()=>{setPayModal(r);setPayForm({amountPaid:r.totalDue||'',paymentDate:new Date().toISOString().slice(0,10),paymentMethod:'cash',transactionId:''});}}><CheckCircleIcon/></button>
                        <button className="act-btn fine"  title="Request Add Fine"   onClick={()=>{setFineModal(r);setFineForm({fineAmount:'',fineReason:''});}}><PlusCircleIcon/></button>
                        <button className="act-btn disc"  title="Request Discount"   onClick={()=>{setDiscModal(r);setDiscForm({discountAmount:'',discountReason:''});}}><TagIcon/></button>
                        <button className="act-btn edit"  title="Request Edit"       onClick={()=>{setEditModal(r);setEditForm({feeAmount:r.feeAmount||'',dueDate:(r.dueDate||'').slice(0,10),status:r.status||'unpaid',totalDue:r.totalDue||'',notes:r.notes||''});}}><PencilSquareIcon/></button>
                        <button className="act-btn print" title="Print Challan"      onClick={()=>handlePrintChallan(r)}><PrinterIcon/></button>
                        <button className="act-btn csv"   title="Export CSV"         onClick={()=>handleExportCSV(r)}><ArrowDownTrayIcon/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="ff-cards">
            {filtered.map(r=>(
              <div key={r._id} className="ff-card">
                <div className="ff-card-top">
                  <div className="name-cell">
                    <div className="av">{ini(r.student?.user?.name)}</div>
                    <div><div className="nm">{r.student?.user?.name||'—'}</div><div className="ns mono">{r.student?.admissionNo}</div></div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <span className={`status-pill ${r.status||'unpaid'}`}>{r.status||'unpaid'}</span>
                    {isOverdue(r)&&<span className="overdue-tag">overdue</span>}
                  </div>
                </div>
                <div className="ff-card-row"><span className="ff-card-label">Class</span>
                  <span>
                    <span className="tag tag-blue">{r.class?.name||'—'}</span>
                    {r.student?.section&&<span className="tag tag-purple" style={{marginLeft:4}}>Sec {r.student.section}</span>}
                  </span>
                </div>
                <div className="ff-card-row"><span className="ff-card-label">Month</span><span className="mono">{monthLabel(r.month)}</span></div>
                <div className="ff-card-row"><span className="ff-card-label">Fee</span><span className="amt-due">{fmtMoney(r.feeAmount)}</span></div>
                <div className="ff-card-row"><span className="ff-card-label">Hostel Fee</span><span className="amt-due">{fmtHostelMoney(r.hostelFee)}</span></div>
                {finesByModule(r.fines,'library').length>0 && <div className="ff-card-row"><span className="ff-card-label">Library Fine</span><FineCell list={finesByModule(r.fines,'library')} chipClass="library" /></div>}
                {finesByModule(r.fines,'hostel').length>0 && <div className="ff-card-row"><span className="ff-card-label">Hostel Fine</span><FineCell list={finesByModule(r.fines,'hostel')} chipClass="hostel" /></div>}
                {otherFines(r.fines).length>0 && <div className="ff-card-row"><span className="ff-card-label">Other Fines</span><FineCell list={otherFines(r.fines)} chipClass="" /></div>}
                <div className="ff-card-row"><span className="ff-card-label">Discount</span>{r.discount ? <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.73rem',color:'#8b5cf6'}}>-{fmtMoney(r.discount)}</span> : <span style={{color:'#c0c5dc',fontSize:'.72rem'}}>—</span>}</div>
                <div className="ff-card-row"><span className="ff-card-label">Total Due</span><span className="amt-due">{fmtMoney(r.totalDue)}</span></div>
                <div className="ff-card-row"><span className="ff-card-label">Paid</span><span className="amt-paid">{fmtMoney(r.amountPaid)}</span></div>
                <div className="ff-card-row"><span className="ff-card-label">Due Date</span><span className="mono" style={{color:isOverdue(r)?'#dc2626':'#6b7194'}}>{fmtDate(r.dueDate)}</span></div>
                {tab==='proofs' && (
                  <div className="ff-card-row"><span className="ff-card-label">Proof</span>
                    {r.paymentProof
                      ? <img src={r.paymentProof} alt="proof" className="proof-img" onClick={()=>setProofModal(r.paymentProof)}/>
                      : <span className="proof-placeholder">No proof</span>}
                  </div>
                )}
                <div className="ff-card-actions">
                  <button className="act-btn pay"   title="Request Mark Paid"  onClick={()=>{setPayModal(r);setPayForm({amountPaid:r.totalDue||'',paymentDate:new Date().toISOString().slice(0,10),paymentMethod:'cash',transactionId:''});}}><CheckCircleIcon/></button>
                  <button className="act-btn fine"  title="Request Add Fine"   onClick={()=>{setFineModal(r);setFineForm({fineAmount:'',fineReason:''});}}><PlusCircleIcon/></button>
                  <button className="act-btn disc"  title="Request Discount"   onClick={()=>{setDiscModal(r);setDiscForm({discountAmount:'',discountReason:''});}}><TagIcon/></button>
                  <button className="act-btn edit"  title="Request Edit"       onClick={()=>{setEditModal(r);setEditForm({feeAmount:r.feeAmount||'',dueDate:(r.dueDate||'').slice(0,10),status:r.status||'unpaid',totalDue:r.totalDue||'',notes:r.notes||''});}}><PencilSquareIcon/></button>
                  <button className="act-btn print" title="Print Challan"      onClick={()=>handlePrintChallan(r)}><PrinterIcon/></button>
                  <button className="act-btn csv"   title="Export CSV"         onClick={()=>handleExportCSV(r)}><ArrowDownTrayIcon/></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Mark Paid Modal ── */}
      {payModal&&createPortal(
        <div className="overlay" onClick={()=>setPayModal(null)}>
          <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Mark Paid — {payModal.student?.user?.name}</div>
              <button className="mo-close" onClick={()=>setPayModal(null)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              <div className="req-note">📋 This will be submitted as a request — admin must approve before it takes effect.</div>
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14}}>
                {[['Month',monthLabel(payModal.month)],['Total Due',fmtMoney(payModal.totalDue)],['Already Paid',fmtMoney(payModal.amountPaid)],['Balance',fmtMoney((payModal.totalDue||0)-(payModal.amountPaid||0))]].map(([l,v])=>(
                  <div key={l} className="info-row"><span className="info-lbl">{l}</span><span className="info-val">{v}</span></div>
                ))}
              </div>
              <div className="form-grid">
                <div className="ff full"><label className="fl">Amount Paid *</label><input type="number" min="0" className="fi" value={payForm.amountPaid} onChange={e=>setPayForm(p=>({...p,amountPaid:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Payment Date</label><input type="date" className="fi" value={payForm.paymentDate} onChange={e=>setPayForm(p=>({...p,paymentDate:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Method</label>
                  <select className="fs" value={payForm.paymentMethod} onChange={e=>setPayForm(p=>({...p,paymentMethod:e.target.value}))}>
                    {['cash','bank_transfer','cheque','online'].map(m=><option key={m} value={m}>{m.replace('_',' ')}</option>)}
                  </select>
                </div>
                <div className="ff full"><label className="fl">Transaction ID</label><input className="fi" placeholder="Optional" value={payForm.transactionId} onChange={e=>setPayForm(p=>({...p,transactionId:e.target.value}))}/></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={()=>setPayModal(null)}>Cancel</button>
              <button className="btn btn-green" onClick={handleMarkPaid} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Add Fine Modal ── */}
      {fineModal&&createPortal(
        <div className="overlay" onClick={()=>setFineModal(null)}>
          <div className="modal" style={{maxWidth:400}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Add Fine — {fineModal.student?.user?.name}</div>
              <button className="mo-close" onClick={()=>setFineModal(null)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              <div className="req-note">📋 Admin must approve before the fine is applied.</div>
              <div className="form-grid">
                <div className="ff full"><label className="fl">Fine Amount (Rs.) *</label><input type="number" min="0" className="fi" value={fineForm.fineAmount} onChange={e=>setFineForm(p=>({...p,fineAmount:e.target.value}))}/></div>
                <div className="ff full"><label className="fl">Reason *</label><input className="fi" required placeholder="e.g. Late fee, Library fine…" value={fineForm.fineReason} onChange={e=>setFineForm(p=>({...p,fineReason:e.target.value}))}/></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={()=>setFineModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleAddFine} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Discount Modal ── */}
      {discModal&&createPortal(
        <div className="overlay" onClick={()=>setDiscModal(null)}>
          <div className="modal" style={{maxWidth:400}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Discount — {discModal.student?.user?.name}</div>
              <button className="mo-close" onClick={()=>setDiscModal(null)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              <div className="req-note">📋 Admin must approve before the discount is applied.</div>
              <div className="form-grid">
                <div className="ff full"><label className="fl">Discount Amount (Rs.) *</label><input type="number" min="0" className="fi" value={discForm.discountAmount} onChange={e=>setDiscForm(p=>({...p,discountAmount:e.target.value}))}/></div>
                <div className="ff full"><label className="fl">Reason</label><input className="fi" required placeholder="e.g. Sibling discount, Merit…" value={discForm.discountReason} onChange={e=>setDiscForm(p=>({...p,discountReason:e.target.value}))}/></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={()=>setDiscModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleDiscount} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Edit Record Modal ── */}
      {editModal&&createPortal(
        <div className="overlay" onClick={()=>setEditModal(null)}>
          <div className="modal" style={{maxWidth:440}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Edit Record — {editModal.student?.user?.name}</div>
              <button className="mo-close" onClick={()=>setEditModal(null)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              <div className="req-note">📋 Admin must approve before the record is updated.</div>
              <div className="form-grid">
                <div className="ff"><label className="fl">Fee Amount (Rs.)</label><input type="number" min="0" className="fi" value={editForm.feeAmount} onChange={e=>setEditForm(p=>({...p,feeAmount:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Total Due (Rs.)</label><input type="number" min="0" className="fi" value={editForm.totalDue} onChange={e=>setEditForm(p=>({...p,totalDue:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Due Date</label><input type="date" className="fi" value={editForm.dueDate} onChange={e=>setEditForm(p=>({...p,dueDate:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Status</label>
                  <select className="fs" value={editForm.status} onChange={e=>setEditForm(p=>({...p,status:e.target.value}))}>
                    {['unpaid','paid','partial','waived'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="ff full"><label className="fl">Notes</label><input className="fi" value={editForm.notes} onChange={e=>setEditForm(p=>({...p,notes:e.target.value}))}/></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={()=>setEditModal(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleEditRecord} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Bulk Generate Modal ── */}
      {bulkModal&&createPortal(
        <div className="overlay" onClick={()=>setBulkModal(false)}>
          <div className="modal" style={{maxWidth:460}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Request: Bulk Generate Fee Records</div>
              <button className="mo-close" onClick={()=>setBulkModal(false)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              <div className="req-note">📋 Admin must approve before fee records are generated.</div>
              <div className="form-grid">
                <div className="ff"><label className="fl">Class *</label>
                  <select className="fs" value={bulkForm.classId} onChange={e=>setBulkForm(p=>({...p,classId:e.target.value,section:''}))}>
                    <option value="">Select class…</option>
                    {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="ff"><label className="fl">Section</label>
                  <select className="fs" value={bulkForm.section} onChange={e=>setBulkForm(p=>({...p,section:e.target.value}))} disabled={!bulkForm.classId}>
                    <option value="">All sections</option>
                    {(classes.find(c=>String(c._id)===String(bulkForm.classId))?.sections||[]).map(s=><option key={s} value={s}>Section {s}</option>)}
                  </select>
                </div>
                <div className="ff"><label className="fl">Month *</label><input type="month" className="fi" value={bulkForm.month} onChange={e=>setBulkForm(p=>({...p,month:e.target.value}))}/></div>
                <div className="ff"><label className="fl">Due Date</label><input type="date" className="fi" value={bulkForm.dueDate} onChange={e=>setBulkForm(p=>({...p,dueDate:e.target.value}))}/></div>
                <div className="ff full"><label className="fl">Fee Amount (Rs.) *</label><input type="number" min="0" className="fi" value={bulkForm.feeAmount} onChange={e=>setBulkForm(p=>({...p,feeAmount:e.target.value}))}/></div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={()=>setBulkModal(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleBulkGenerate} disabled={saving}>{saving?'Submitting…':'Submit Request'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Proof Image Modal ── */}
      {proofModal&&createPortal(
        <div className="overlay" onClick={()=>setProofModal(null)}>
          <div style={{background:'white',borderRadius:16,padding:20,maxWidth:600,width:'100%',position:'relative'}} onClick={e=>e.stopPropagation()}>
            <button onClick={()=>setProofModal(null)} style={{position:'absolute',top:12,right:12,width:30,height:30,borderRadius:8,border:'none',background:'#f0f2f7',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><XMarkIcon style={{width:14,height:14,strokeWidth:2}}/></button>
            <img src={proofModal} alt="Payment Proof" style={{width:'100%',borderRadius:10,maxHeight:'80vh',objectFit:'contain'}}/>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}